import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { AudioAssetDto } from "../api/types";
import { formatBytes } from "../lib/format";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

/**
 * Starting a new project from an existing one.
 *
 * The references a producer re-uses across a series — Product ID, the review
 * link and its password, the Box folder, their own essentials — cost nothing to
 * copy and are the reason to duplicate at all. They always travel.
 *
 * The book's own documents and its audio are opt-in and default OFF, because
 * the common case is the NEXT title for the same client rather than a second
 * copy of this one. Audio is copied a file at a time: on object storage each
 * copy is a download and a re-upload, so a book in one request would hold it
 * open for minutes and then fail.
 */
export function DuplicateProjectDialog({ projectId, projectName, onClose, onDone }: {
  projectId: string;
  projectName: string;
  onClose: () => void;
  /** Told about the new project once it exists, so the surface that opened this
   *  can show it in ITS OWN terms. Duplicating no longer navigates: you stay
   *  where you were working, which is only useful if the result turns up
   *  there. */
  onDone?: (id: string, name: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(`${projectName} (copy)`);
  const [manuscript, setManuscript] = useState(false);
  const [audio, setAudio] = useState(false);
  const [proofing, setProofing] = useState(false);
  const [assets, setAssets] = useState<AudioAssetDto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const gone = useRef(false);

  // Reset on the way in as well as setting on the way out: React's development
  // double-invoke would otherwise latch this before the copy ever starts.
  useEffect(() => {
    gone.current = false;
    return () => { gone.current = true; };
  }, []);

  useEffect(() => {
    api<{ assets: AudioAssetDto[] }>(`/api/projects/${projectId}/audio`)
      .then((r) => setAssets(r.assets))
      .catch(() => setAssets([]));
  }, [projectId]);

  const n = assets?.length ?? 0;
  const bytes = (assets ?? []).reduce((a, x) => a + (x.size_bytes || 0), 0);
  const anyProofed = (assets ?? []).some((a) => a.status === "transcribed");

  const go = async () => {
    setBusy(true);
    try {
      const made = await api<{ id: string; name: string; audio: string[]; include_proofing: boolean }>(
        `/api/projects/${projectId}/duplicate`,
        {
          method: "POST",
          body: JSON.stringify({
            name: name.trim() || undefined,
            include_manuscript: manuscript,
            include_audio: audio,
            include_proofing: audio && proofing,
          }),
        },
      );

      setTotal(made.audio.length);
      for (let i = 0; i < made.audio.length; i++) {
        if (gone.current) return;
        await api(`/api/projects/${projectId}/duplicate/file`, {
          method: "POST",
          body: JSON.stringify({
            target_project_id: made.id, filename: made.audio[i],
            include_proofing: made.include_proofing,
          }),
        });
        setDone(i + 1);
      }

      qc.invalidateQueries({ queryKey: ["projects"] });
      onDone?.(made.id, made.name);
      toast(`“${made.name}” created.`, "success");
      onClose();
    } catch (e: any) {
      // The project may already exist with some of its files: say so rather
      // than implying nothing happened.
      toast(e?.message || "Couldn't finish duplicating — check the projects list.", "error");
      qc.invalidateQueries({ queryKey: ["projects"] });
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" {...backdropClose(() => { if (!busy) onClose(); })}>
      <div className="modal-card dup" role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}>
        <h3>Duplicate project</h3>
        <p className="dup-lede">
          The new project starts with this one’s Product ID, review link and password,
          Box folder and essentials. Choose what else to bring.
        </p>

        <label className="label" htmlFor="dup-name">Name</label>
        <input id="dup-name" className="input" value={name} disabled={busy}
          onChange={(e) => setName(e.target.value)} autoFocus />

        <div className="dup-opts">
          <label className={`dup-opt${busy ? " is-off" : ""}`}>
            <input type="checkbox" checked={manuscript} disabled={busy}
              onChange={(e) => setManuscript(e.target.checked)} />
            <span>
              <b>Manuscript and Table of Contents</b>
              <em>The book’s own documents. Leave off for a different title.</em>
            </span>
          </label>

          <label className={`dup-opt${busy || !n ? " is-off" : ""}`}>
            <input type="checkbox" checked={audio} disabled={busy || !n}
              onChange={(e) => { setAudio(e.target.checked); if (!e.target.checked) setProofing(false); }} />
            <span>
              <b>Audio files{n ? ` (${n} · ${formatBytes(bytes)})` : ""}</b>
              <em>{n ? "Copied one at a time, so a book takes a moment." : "This project has no audio."}</em>
            </span>
          </label>

          <label className={`dup-opt dup-sub${busy || !audio || !anyProofed ? " is-off" : ""}`}>
            <input type="checkbox" checked={proofing} disabled={busy || !audio || !anyProofed}
              onChange={(e) => setProofing(e.target.checked)} />
            <span>
              <b>Proofing and pickups</b>
              <em>{anyProofed
                ? "Needs the audio: a report describes the files it was run against."
                : "Nothing is proofed in this project yet."}</em>
            </span>
          </label>
        </div>

        {busy && total > 0 && (
          <div className="dup-prog">
            <div className="rfs-bar"><span style={{ width: `${(done / total) * 100}%` }} /></div>
            <span className="mono">Copying audio — {done} / {total}</span>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={go} disabled={busy || !name.trim()}>
            {busy ? "Duplicating…" : "Duplicate"}
          </button>
        </div>
      </div>
    </div>
  );
}
